import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';

type Size = 'xs' | 'sm' | 'md' | 'lg';

const SIZES: Record<Size, number> = { xs: 12, sm: 14, md: 18, lg: 28 };

/**
 * Stars, shown rather than input.
 *
 * Partial fill is done with a clipped overlay rather than half-star glyphs, so 4.3 reads as 4.3
 * instead of rounding to 4.5 — on a marketplace the difference between those two numbers is the
 * difference between a car people trust and one they do not.
 */
export function StarRating({
  value,
  size = 'sm',
  className,
}: {
  value: number;
  size?: Size;
  className?: string;
}) {
  const pixels = SIZES[size];

  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} aria-hidden>
      {[1, 2, 3, 4, 5].map((star) => {
        // How much of this particular star is filled, 0 to 1.
        const fill = Math.max(0, Math.min(1, value - star + 1));

        return (
          <span key={star} className="relative inline-flex shrink-0">
            <Star size={pixels} className="text-[var(--border-strong)]" />
            {fill > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <Star size={pixels} className="fill-gold text-gold" />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

const LABELS = ['', 'Loše', 'Slabo', 'Solidno', 'Dobro', 'Odlično'];

/**
 * The same stars, but you can press them.
 *
 * Hover previews the value it would set, which is the only affordance that makes a five-star
 * control legible without a legend — and the word underneath says what the number means, because
 * "3" means different things to different people.
 */
export function StarInput({
  value,
  onChange,
  size = 'lg',
  disabled,
  className,
}: {
  value: number;
  onChange: (value: number) => void;
  size?: Size;
  disabled?: boolean;
  className?: string;
}) {
  const [preview, setPreview] = useState<number | null>(null);
  const shown = preview ?? value;
  const pixels = SIZES[size];

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div
        className="flex items-center gap-1"
        role="radiogroup"
        aria-label="Ocena"
        onMouseLeave={() => setPreview(null)}
      >
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} ${star === 1 ? 'zvezdica' : 'zvezdice'} — ${LABELS[star]}`}
            disabled={disabled}
            onClick={() => onChange(star)}
            onMouseEnter={() => setPreview(star)}
            onFocus={() => setPreview(star)}
            onBlur={() => setPreview(null)}
            className={cn(
              'press rounded-lg p-0.5 transition duration-200 disabled:cursor-not-allowed disabled:opacity-50',
              !disabled && 'hover:scale-110',
            )}
          >
            <Star
              size={pixels}
              className={cn(
                'transition duration-200',
                star <= shown ? 'fill-gold text-gold' : 'text-[var(--border-strong)]',
              )}
            />
          </button>
        ))}
      </div>

      {/* Fixed width so the row does not jump as the word under the cursor changes length. */}
      <span className="min-w-16 text-sm font-medium text-muted">{LABELS[shown] ?? ''}</span>
    </div>
  );
}

/** The compact "4,3 (12)" that goes on a card or beside a name. */
export function RatingBadge({
  value,
  count,
  className,
}: {
  value?: number | null;
  count: number;
  className?: string;
}) {
  if (!value || count === 0) {
    return null;
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-gold/12 px-2 py-0.5 text-[11px] font-semibold text-gold',
        className,
      )}
    >
      <Star size={11} className="fill-current" />
      <span className="tabular-nums">{value.toFixed(1).replace('.', ',')}</span>
      <span className="font-normal opacity-70 tabular-nums">({count})</span>
    </span>
  );
}
